/*
  This code is the intellectual property of Bulb Inc. and other software publishers;
  it may not be altered, copied or disclosed without prior written approval.
  Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import {
  EventEmitter, Inject, Injectable, OnDestroy,
} from '@angular/core';
import Uppy from '@uppy/core';
import type Dashboard from '@uppy/dashboard';
import { Subscription } from 'rxjs';
import { BulbCKEStateService } from 'src/ckeditor/editor/services/bulb-cke-state.service';
import { ErrorService } from 'src/common/error.service';
import { BULB_BUSY_COVER_SERVICE } from '../angularjs.providers';
import { AssetUploadEventService } from './asset-upload-event.service';
import { AssetUploadHttpService } from './http.service';
import { AeroDialogService } from '../../aero/aero-dialog/aero-dialog.service';
import {
  BulbConfigurableOptions,
  CompanionUploadRequest,
  CompanionUploadResponse,
  UppyAllowedFileTypes,
  UppyComponentEvent,
  UppyComponentEventStatus,
  UppyComponentEventType,
} from './types';
import { UppyCoreService } from './uppy-core.service';
import { UppyDashboardPluginsService } from './uppy-dashboard-plugins.service';
import {
  buildCompanionUploadRequest,
  buildOnCompleteEvent,
  buildOnSuccessEvent,
} from './uppy-dashboard/uppy-dashboard-utilities';

/**
 * This service is responsible for opening the Uppy Dashboard and
 * handling the events emitted by the Uppy Dashboard.
 *
 * Creates a new instance of Uppy and an Event Emitter to be used by
 * components and other services.
 */
@Injectable({
  providedIn: 'root',
})
export class UppyDashboardService implements OnDestroy {
  public readonly uppyEventEmitter: EventEmitter<UppyComponentEvent> = new EventEmitter<UppyComponentEvent>();

  private uppy: Uppy;

  private options: BulbConfigurableOptions = {
    multiSelect: false,
    allowedFileTypes: [UppyAllowedFileTypes.ANY],
    assetLibrary: true,
    embed: false,
  };

  private httpCompanionUpload: Subscription;

  hideBusy = null;

  /**
   * Stores details of the uploaded image assets.
   */
  uploadedAssets = [];

  constructor(
    private httpSvc: AssetUploadHttpService,
    private aeroDialogService: AeroDialogService,
    public eventBus: AssetUploadEventService,
    private uppySvc: UppyCoreService,
    private uppyDashboardPluginService: UppyDashboardPluginsService,
    private bulbCkeStateService: BulbCKEStateService,
    private errorService: ErrorService,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService,
    @Inject('$translate') private $translate: any,
    @Inject('BulbAuthService') private bulbAuthService,
  ) {
    this.uppyEventEmitter.subscribe((event: UppyComponentEvent) => {
      if (event.type === UppyComponentEventType.ON_COMPLETE && !event.error) {
        this.handleUploadCompletion(event);
        // This request has a different type when we are emitting a processing event.
        // Need to figure out how to organize our event alongside Uppy's.

      } else if (!this.options.multiSelect && event.type === UppyComponentEventType.ON_SUCCESS
        && event.response?.meta) {
        if (this.options.uploadState === 'page' || this.options.uploadState === 'imageCarousel' || this.options.uploadState === 'imageCarouselSlide') {
          if (!this.hideBusy) {
            this.hideBusy = this.bulbBusyCoverService.showBusy();
            this.bulbCkeStateService.setDesktopUploadSelectionMode(true);
          }
        }
        this.addToAssetLibrary(event.response, this.options);
      }
    });
  }

  /**
   * Inits the uppy dashboard if it hasn't been
   * Opens the Uppy Dashboard modal.
   */
  public async open(options: BulbConfigurableOptions): Promise<void> {
    this.options = options;
    await this.initializeUppy(options);
    this.getDashboardPlugin()?.openModal();
  }

  async initializeUppy(options: BulbConfigurableOptions): Promise<any> {
    if (!this.storageLimitExceeded()) {
    // The Uppy Instance configures with the Dashboard, S3, and Companion Plugins.
      this.uppy = await this.uppySvc.initializeUppy({
        maxNumberOfFiles: options.multiSelect ? 20 : 1,
        allowedFileTypes: options.allowedFileTypes,
        autoProceed: false,
      });
      this.registerUppyEventListeners(this.uppy);

      await this.uppyDashboardPluginService.useUppyDashboardModal(this.uppy);
      await this.uppyDashboardPluginService.useCoreDashboardPlugins(this.uppy);
      // To ensure our custom plugins are loaded after the core plugins, we need to wait for the core plugins to load.
      const supportsImages = Boolean(
        options.allowedFileTypes?.includes(UppyAllowedFileTypes.IMAGE)
        || options.allowedFileTypes?.includes(UppyAllowedFileTypes.IMAGE_EXTENSION),
      );

      const supportsCanva = supportsImages || !(
        options.allowedFileTypes?.includes(UppyAllowedFileTypes.AUDIO)
          || options.allowedFileTypes?.includes(UppyAllowedFileTypes.VIDEO)
      );

      await this.uppyDashboardPluginService.useCustomDashboardPlugins(
        this.uppy,
        options.folderId,
        options.assetLibrary,
        options.embed,
        this.options.uploadState,
        this.options.contentBlockData,
        supportsImages && options.useAdobeExpressPlugin !== false,
        supportsCanva && options.useCanvaPlugin !== false,
      );
      // Load these last, so they appear last in order per requirements.
      this.uppyDashboardPluginService.useOptionalDashboardPlugins(this.uppy, options);
    }
  }

  async initializeUppyForAutoProceed(
    options: BulbConfigurableOptions,
    autoProceed ?: boolean,
    file?: any,
  ): Promise<any> {
    this.options = options;
    this.hideBusy = this.bulbBusyCoverService.showBusy();
    this.uppy = await this.uppySvc.initializeUppy({
      maxNumberOfFiles: options.multiSelect ? 20 : 1,
      allowedFileTypes: options.allowedFileTypes,
      autoProceed,
    });
    this.registerUppyEventListeners(this.uppy);
    // Add the file to the queue:
    try {
      const fileObject : any = {
        source: 'Local',
        name: file.name,
        type: file.type,
        data: file, // file blob
      };
      // Adding the selected file to Uppy's internal state.
      const fileList = [];
      const fileId = this.uppy.addFile(fileObject);
      fileList.push(fileId);
    } catch (error) {
      this.errorService.handleException(error);
    }
  }

  private registerUppyEventListeners(uppyInstance): void {
    uppyInstance
      .on('upload-success', this.handleOnSuccess.bind(this))
      .on('upload-error', this.eventBus.handleOnError.bind(this))
      .on('complete', this.handleOnComplete.bind(this))
      .on('dashboard:modal-closed', this.cleanupAndClose.bind(this))
      .on('file-added', (file) => {
        // BULBAPP-9430: for handling this ticket, we need to remove ADD MORE button from uppy dashboard.
        // The ADD MORE button only visible when user done selecting file/files. So we added uppy's file-added event.
        // timeout is required because it will take a little while for add more button to be added to DOM.
        setTimeout(() => {
          const element = document.querySelector('.uppy-DashboardContent-addMore') as HTMLElement;
          if (element) {
            element.style.display = 'none';
          }
        });
      });
  }

  private getDashboardPlugin(): Dashboard | undefined {
    return this.uppy?.getPlugin('Dashboard') as unknown as Dashboard | undefined;
  }

  /**
   * Check if current user exceeds or at the storage limit or not.
   * Opens the storage limit reached modal.
   */
  public storageLimitExceeded() {
    const storage = this.bulbAuthService.currentUser?.features?.StorageFeature;
    if (storage?.maxStorageBytes !== -1 && storage?.currentUsedStorageBytes >= storage?.maxStorageBytes) {
      const data = {
        title: this.$translate.instant('soft_delete_storage_limit_heading'),
        detail: this.$translate.instant('storage_limit_reached_details'),
      };
      this.aeroDialogService.showPlanUpgradeDialog(data);
      return true;
    }
    return false;
  }

  public handleOnComplete(response: any) {
    this.uppyEventEmitter.emit(buildOnCompleteEvent(response));
  }

  public handleOnSuccess(response: any) {
    this.uppyEventEmitter.emit(buildOnSuccessEvent(UppyComponentEventStatus.SUCCESS, response));
  }

  public handleOnProcessingSuccess(response: any) {
    this.uppyEventEmitter.emit(buildOnSuccessEvent(UppyComponentEventStatus.PROCESSING_SUCCESS, response));
  }

  private addToAssetLibrary(response: any, options: BulbConfigurableOptions) {
    if (!this.hideBusy) {
      this.hideBusy = this.bulbBusyCoverService.showBusy();
    }
    const request: CompanionUploadRequest = buildCompanionUploadRequest(response, options.folderId);
    this.httpCompanionUpload = this.httpSvc.companionUpload(request).subscribe(
      (uploadResponse: CompanionUploadResponse) => {
        if (this.hideBusy) {
          this.hideBusy();
          this.hideBusy = null;
        }
        if (options.uploadState === 'page' && this.bulbCkeStateService.getDesktopUploadSelectionMode()) {
          if (this.hideBusy) {
            this.hideBusy();
          }
          if (response.meta && response.meta.nextIndex !== undefined) {
            uploadResponse.nextIndex = response.meta.nextIndex;
            uploadResponse.hasMoreFiles = response.meta.hasMoreFiles;
          }
          const uploadStartedEvent = new CustomEvent('assetLibraryUploadStarted', {
            detail: {
              uploadData: uploadResponse,
            },
          });
          if (this.bulbCkeStateService.assetType.value !== 'IMAGE') {
            this.bulbCkeStateService.assetLibraryUploadEvent.next(uploadStartedEvent);
          }
        } else if ((options.uploadState === 'imageCarousel' || options.uploadState === 'imageCarouselSlide')
          && this.bulbCkeStateService.getDesktopUploadSelectionMode()) {
          this.uploadedAssets.push(uploadResponse.entry.id);
          if (response.meta.hasMoreFiles) {
            this.bulbCkeStateService.multipleUploadStart.next(response.meta.nextIndex);
            return;
          }
          CKEBulbData.service.ckeStateService.multipleUploadStart.next(-1);
          uploadResponse.uploadedAssets = this.uploadedAssets;
          this.uploadedAssets = [];
        }
        this.eventBus.handleOnProcessingSuccess(uploadResponse, options.uploadState, options.contentBlockData);
      },
      (err) => {
        const message = err.error.developerMessage || [];
        if (message.includes('TIMEOUT')) {
          this.handleProcessingTimeout(err);
        } else {
          this.eventBus.handleOnProcessingFailure(err);
        }
      },
    );
  }

  /**
   * Show warning modal when there is a processing timeout error.
   *
   * @param error error while image processing.
   * @private
   */
  private handleProcessingTimeout(error) {
    this.hideBusy();
    this.hideBusy = null;
    this.bulbCkeStateService.multipleUploadStart.next(-1);
    this.uppyEventEmitter.emit(buildOnSuccessEvent(UppyComponentEventStatus.PROCESSING_FAILURE, error));
    this.aeroDialogService.showDialog({
      icon: 'fas fa-exclamation-triangle',
      iconClass: 'aero-alert',
      detail: this.$translate.instant('image_processing_timeout'),
      primaryLabel: this.$translate.instant('button_ok'),
      showSecondary: false,
    });
  }

  public cleanupAndClose() {
    // If this service was destroyed without initializing Uppy, we don't need to clean up.
    if (!this.uppy) return;
    // Resets the Uppy Modal and closes it if no errors occurred.
    setTimeout(() => {
      if (this && this.uppy) {
        this.uppy.cancelAll();
        this.getDashboardPlugin()?.closeModal();
        // Reset uppy initialization to avoid hanging instance
        this.uppy = undefined;
      }
    }, 500);
  }

  ngOnDestroy() {
    this.hideBusy = null;
    this.uploadedAssets = [];
    this.cleanupAndClose();
    this.httpCompanionUpload?.unsubscribe();
    this.uppyEventEmitter.unsubscribe();
  }

  /**
   * Will call on upload completion. And handle multiple select upload as well.
   *
   * @param event Upload completion event
   * @private
   */
  private handleUploadCompletion(event) {
    const response = event.success;
    // This will work in case user is on page and click add image
    if (this.options.multiSelect) {
      this.bulbCkeStateService.insertParagraphSubject.next(false);
      const subscribeUpload = this.bulbCkeStateService.multipleUploadStart.subscribe((value) => {
        if (value === undefined || value === -1) {
          return;
        }

        // Disable save to handle multiple image insertion. Save will be called after all image insertion.
        this.bulbCkeStateService.handleSaveOperation.next(false);
        if (this.options.uploadState === 'page' || this.options.uploadState === 'imageCarousel'
          || this.options.uploadState === 'imageCarouselSlide') {
          this.cleanupAndClose();
          if (!this.hideBusy) {
            this.hideBusy = this.bulbBusyCoverService.showBusy();
            this.bulbCkeStateService.setDesktopUploadSelectionMode(true);
          }
        }
        const uploaded : any = response[value];

        uploaded.meta.hasMoreFiles = (value < (response.length - 1));
        uploaded.meta.nextIndex = uploaded.meta.hasMoreFiles ? (value + 1) : -1;
        this.addToAssetLibrary(response[value], this.options);

        if ((response.length - 1) === value) {
          if (subscribeUpload) {
            subscribeUpload.unsubscribe();
          }
        }
      });
      this.bulbCkeStateService.multipleUploadStart.next(0);
    } else {
      this.cleanupAndClose();
    }
  }
}
